import React, { useState, useEffect } from "react";
import { Box, styled, useMediaQuery, useTheme } from "@mui/material";

interface Step {
  label: string;
  description: string;
}

interface ProfileWizardStepsProps {
  steps: Step[];
  activeStep: number;
  onStepClick: (stepIndex: number) => void;
  isDarkMode: boolean;
}

const ProfileWizardSteps = ({
  steps,
  activeStep,
  onStepClick,
  isDarkMode
}: ProfileWizardStepsProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [textConfig, setTextConfig] = useState<any>({});

  const fetchTextConfig = async () => {
    try {
      const response = await fetch("/text.json");
      const data = await response.json();
      setTextConfig(data);
    } catch (error) {
      console.error("Error loading text config:", error);
    }
  };

  useEffect(() => {
    fetchTextConfig();
  }, []);

  const ToggleContainer = styled(Box)({
    width: "100%",
    backgroundColor: isDarkMode ? "#18181B" : "#f1f5f9",
    padding: "4px",
    borderRadius: "8px",
    display: "flex",
    gap: "4px",
    marginBottom: "16px",
    position: "relative",
  });
  
  const Slider = styled(Box)({
    position: "absolute",
    width: `calc(${100 / steps.length}% - 6px)`,
    height: "calc(100% - 8px)",
    backgroundColor: "#22C55E",
    borderRadius: "6px",
    transition: "transform 0.3s ease-in-out",
    transform: `translateX(${activeStep * 100}%)`,
    left: "4px",
    top: "4px",
  });

  const StepButton = styled(Box)<{ active?: boolean; completed?: boolean }>(
    ({ active, completed }) => ({
      flex: 1,
      padding: "8px",
      textAlign: "center",
      color: active 
        ? "#ffffff" 
        : completed 
          ? (isDarkMode ? "#22C55E" : "#16A34A") 
          : (isDarkMode ? "#FFFFFF" : "#64748b"),
      cursor: "pointer",
      borderRadius: "6px",
      position: "relative",
      zIndex: 1,
      transition: "color 0.2s ease-in-out",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: active || completed ? 600 : 400,
      fontSize: isMobile ? "8px" : "14px",
      "&:hover": {
        color: active 
          ? "#ffffff"
          : completed 
            ? (isDarkMode ? "#22C55E" : "#16A34A")
            : (isDarkMode ? "#22C55E" : "#16A34A")
      }
    })
  );

  const handleStepClick = (index: number) => {
    if (index < activeStep) {
      onStepClick(index);
    }
  };

  // Get localized steps with fallback to original if textConfig not loaded
  const getLocalizedSteps = () => {
    if (!textConfig.PROFILE_WIZARD_STEPS) {
      return steps;
    }

    return steps.map((step, index) => {
      const stepKey = `STEP_${index + 1}`;
      const localizedStep = textConfig.PROFILE_WIZARD_STEPS[stepKey];
      
      return {
        label: localizedStep?.LABEL || step.label,
        description: localizedStep?.DESCRIPTION || step.description
      };
    });
  };

  const localizedSteps = getLocalizedSteps();

  return (
    <ToggleContainer>
      <Slider />
      {localizedSteps.map((step, index) => (
        <StepButton
          key={index}
          active={activeStep === index}
          completed={activeStep > index}
          onClick={() => handleStepClick(index)}
          title={step.description}
        >
          {step.label}
        </StepButton>
      ))}
    </ToggleContainer>
  );
};

export default ProfileWizardSteps;